import React, { useState } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const InsuranceTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  const initialFormState = {
    company: "",
    planName: "",
    policyNo: "",
    emergencyContact: "",
    price: "",
    link: "",
    notes: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  const handleEditClick = (item) => {
    setFormData({ ...initialFormState, ...item });
    setEditingId(item.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormState);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const itemData = {
        ...formData,
        id: editingId || `ins_${Date.now()}`,
        price: parseFloat(formData.price || 0),
        order: editingId
          ? trip.insurances?.find((i) => i.id === editingId)?.order
          : trip.insurances?.length || 0,
      };

      let newList;
      if (editingId) {
        newList = (trip.insurances || []).map((i) =>
          i.id === editingId ? itemData : i
        );
      } else {
        newList = [...(trip.insurances || []), itemData];
      }

      await updateDoc(doc(db, "trips", trip.id), { insurances: newList });
      onUpdateTrip(newList, "insurances");
      setForceRender((prev) => prev + 1);
      handleCancel();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบประกันนี้ใช่หรือไม่?")) {
      const newList = (trip.insurances || []).filter((i) => i.id !== id);
      await updateDoc(doc(db, "trips", trip.id), { insurances: newList });
      onUpdateTrip(newList, "insurances");
      setForceRender((prev) => prev + 1);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(sortedItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    try {
      await updateDoc(doc(db, "trips", trip.id), { insurances: updatedItems });
      onUpdateTrip(updatedItems, "insurances");
      setForceRender((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const sortedItems = [...(trip.insurances || [])].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number")
      return a.order - b.order;
    return 0;
  });

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const formBgStyle = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#ddd")}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    marginBottom: "15px",
  };

  return (
    <div style={{ paddingBottom: "30px", maxWidth: "800px", margin: "0 auto" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="insurance-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {sortedItems.map((item, index) => {
                const isEditingThis = editingId === item.id;
                return (
                  <Draggable
                    key={String(item.id)}
                    draggableId={String(item.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        {isEditingThis ? (
                          <div style={formBgStyle}>
                            <form onSubmit={handleSave}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: "15px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    flex: 1,
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="บริษัทประกัน (เช่น MSIG, สมเกียรติประกันภัย)"
                                    value={formData.company}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        company: e.target.value,
                                      })
                                    }
                                    style={{
                                      ...inputStyle,
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                    }}
                                    required
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#e74c3c",
                                    padding: "0 10px",
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "20px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: "1 1 300px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>
                                      แผนประกันภัย
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="เช่น แผน Easy 1"
                                      value={formData.planName}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          planName: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      หมายเลขกรมธรรม์
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="เช่น P-12345678"
                                      value={formData.policyNo}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          policyNo: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      เบอร์ติดต่อฉุกเฉิน
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="เช่น +66 2 123 4567"
                                      value={formData.emergencyContact}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          emergencyContact: e.target.value,
                                        })
                                      }
                                      style={{
                                        ...inputStyle,
                                        fontWeight: "bold",
                                        color: "#e74c3c",
                                      }}
                                    />
                                  </div>
                                </div>

                                <div
                                  style={{
                                    flex: "1 1 300px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                    borderLeft: `1px solid ${
                                      isDarkMode ? "#444" : "#ddd"
                                    }`,
                                    paddingLeft: "20px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>
                                      ค่าใช้จ่าย (฿)
                                    </label>
                                    <input
                                      type="number"
                                      step="any"
                                      value={formData.price}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          price: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      บันทึกเพิ่มเติม
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="ข้อมูลความคุ้มครองคร่าวๆ"
                                      value={formData.notes}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          notes: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      🔗 ลิงก์เอกสารกรมธรรม์ (E-Policy)
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="วางลิงก์ที่นี่..."
                                      value={formData.link}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          link: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginTop: "20px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={handleCancel}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                    border: `1px solid ${
                                      isDarkMode ? "#555" : "#ccc"
                                    }`,
                                    background: isDarkMode ? "#333" : "white",
                                    color: isDarkMode ? "#fff" : "#000",
                                    cursor: "pointer",
                                  }}
                                >
                                  ยกเลิก
                                </button>
                                <button
                                  type="submit"
                                  style={{
                                    padding: "8px 24px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#6c5ce7",
                                    color: "white",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  บันทึก
                                </button>
                              </div>
                            </form>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "15px",
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                color: "#bbb",
                                marginTop: "15px",
                                cursor: "grab",
                              }}
                            >
                              ⋮⋮
                            </div>

                            <div
                              style={{
                                flex: 1,
                                backgroundColor:
                                  theme?.cardBg ||
                                  (isDarkMode ? "#1e1e1e" : "#fff"),
                                borderRadius: "12px",
                                padding: "18px 20px",
                                border: `1px solid ${
                                  snapshot.isDragging
                                    ? "#6c5ce7"
                                    : theme?.border || "#eee"
                                }`,
                                borderLeft: "4px solid #6c5ce7",
                                position: "relative",
                                transition: "all 0.2s",
                                boxShadow: snapshot.isDragging
                                  ? "0 10px 20px rgba(0,0,0,0.15)"
                                  : "none",
                              }}
                            >
                              <button
                                onClick={() => handleEditClick(item)}
                                style={{
                                  position: "absolute",
                                  top: "15px",
                                  right: "15px",
                                  background: "none",
                                  border: "none",
                                  color: "#888",
                                  fontSize: "18px",
                                  cursor: "pointer",
                                }}
                              >
                                •••
                              </button>

                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "900",
                                  color: theme?.text || "#333",
                                  marginBottom: "8px",
                                  paddingRight: "30px",
                                }}
                              >
                                🛡️ {item.company}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "20px",
                                  flexWrap: "wrap",
                                  marginBottom: "15px",
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#888",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    แผนประกัน
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: theme?.text,
                                    }}
                                  >
                                    {item.planName || "-"}
                                  </div>
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#888",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    เลขกรมธรรม์
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: "#3b82f6",
                                    }}
                                  >
                                    {item.policyNo || "-"}
                                  </div>
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#888",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    ฉุกเฉิน (Emergency)
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: "#e74c3c",
                                    }}
                                  >
                                    {item.emergencyContact || "-"}
                                  </div>
                                </div>
                              </div>

                              {item.notes && (
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#888",
                                    marginBottom: "10px",
                                  }}
                                >
                                  {item.notes}
                                </div>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  borderTop: `1px dashed ${
                                    isDarkMode ? "#444" : "#eee"
                                  }`,
                                  paddingTop: "10px",
                                }}
                              >
                                <div>
                                  {item.link && (
                                    <a
                                      href={item.link}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: "#6c5ce7",
                                        textDecoration: "none",
                                        backgroundColor:
                                          "rgba(108, 92, 231, 0.1)",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      📄 ดูกรมธรรม์
                                    </a>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    color: "#0984e3",
                                  }}
                                >
                                  {item.price > 0
                                    ? `฿${Number(item.price).toLocaleString()}`
                                    : "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {isAdding && (
        <div
          style={{
            ...formBgStyle,
            border: `2px dashed #6c5ce7`,
            marginTop: "10px",
          }}
        >
          <h4 style={{ margin: "0 0 15px 0", color: "#6c5ce7" }}>
            🛡️ เพิ่มประกันเดินทาง
          </h4>
          <form onSubmit={handleSave}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "15px",
              }}
            >
              <div style={{ display: "flex", gap: "10px", flex: 1 }}>
                <input
                  type="text"
                  placeholder="บริษัทประกัน (เช่น MSIG, สมเกียรติประกันภัย)"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  style={{
                    ...inputStyle,
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                  required
                />
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              <div
                style={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <div>
                  <label style={labelStyle}>แผนประกันภัย</label>
                  <input
                    type="text"
                    placeholder="เช่น แผน Easy 1"
                    value={formData.planName}
                    onChange={(e) =>
                      setFormData({ ...formData, planName: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>หมายเลขกรมธรรม์</label>
                  <input
                    type="text"
                    placeholder="เช่น P-12345678"
                    value={formData.policyNo}
                    onChange={(e) =>
                      setFormData({ ...formData, policyNo: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>เบอร์ติดต่อฉุกเฉิน</label>
                  <input
                    type="text"
                    placeholder="เช่น +66 2 123 4567"
                    value={formData.emergencyContact}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergencyContact: e.target.value,
                      })
                    }
                    style={{
                      ...inputStyle,
                      fontWeight: "bold",
                      color: "#e74c3c",
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  borderLeft: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                  paddingLeft: "20px",
                }}
              >
                <div>
                  <label style={labelStyle}>ค่าใช้จ่าย (฿)</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>บันทึกเพิ่มเติม</label>
                  <input
                    type="text"
                    placeholder="ข้อมูลความคุ้มครองคร่าวๆ"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    🔗 ลิงก์เอกสารกรมธรรม์ (E-Policy)
                  </label>
                  <input
                    type="text"
                    placeholder="วางลิงก์ที่นี่..."
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "20px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                  background: isDarkMode ? "#333" : "white",
                  color: isDarkMode ? "#fff" : "#000",
                  cursor: "pointer",
                }}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                style={{
                  padding: "8px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#6c5ce7",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                บันทึก
              </button>
            </div>
          </form>
        </div>
      )}

      {!isAdding && !editingId && (
        <button
          onClick={() => {
            setFormData(initialFormState);
            setIsAdding(true);
          }}
          style={{
            padding: "15px",
            borderRadius: "12px",
            border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
            background: "transparent",
            color: isDarkMode ? "#aaa" : "#666",
            fontSize: "15px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "10px",
            transition: "0.2s",
          }}
        >
          ➕ เพิ่มประกันเดินทาง
        </button>
      )}
    </div>
  );
};

export default InsuranceTab;
